import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { formatTimestamp } from '../../utils/helpers';
import { SaveState } from '../../types/firebase';
import { ThemeToggle } from '../Common/ThemeToggle';
import Button from '../Common/Button';
import './Header.css';

interface HeaderProps {
  saveState?: SaveState;
  onSave?: () => void;
  onNavigateToCharacterSelect?: () => void;
  showCharacterSelect?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  saveState,
  onSave,
  onNavigateToCharacterSelect,
  showCharacterSelect = false
}) => {
  const { user, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getSaveStatusText = () => {
    if (!saveState) return '';

    switch (saveState.status) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return saveState.lastSaved
          ? `Saved at ${formatTimestamp(saveState.lastSaved)}`
          : 'Saved';
      case 'modified':
        return 'Unsaved changes';
      case 'error':
        return `Error: ${saveState.error}`;
      default:
        return '';
    }
  };

  const getSaveStatusClass = () => {
    if (!saveState) return '';
    return `header__save-status--${saveState.status}`;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <header className="header">
      <div className="header__container">
        {/* App Title */}
        <div className="header__title">
          <h1>Character Tracker</h1>
        </div>

        {/* Desktop: Theme toggle and user info */}
        <div className="header__desktop">
          <div className="header__theme-toggle">
            <ThemeToggle variant="toggle" showLabels={false} size="small" />
          </div>

          {user && (
            <div className="header__user-info">
              <div className="header__user-content">
                <span className="header__user-name">
                  {user.displayName || (user.isAnonymous ? 'Guest' : 'User')}
                </span>
                {saveState && (
                  <span className={`header__save-status ${getSaveStatusClass()}`}>
                    {getSaveStatusText()}
                  </span>
                )}
              </div>

              <div className="header__user-actions">
                {showCharacterSelect && onNavigateToCharacterSelect && (
                  <Button
                    variant="SECONDARY"
                    size="SMALL"
                    onClick={onNavigateToCharacterSelect}
                  >
                    👤 Characters
                  </Button>
                )}
                {onSave && saveState?.status === 'modified' && (
                  <Button
                    variant="PRIMARY"
                    size="SMALL"
                    onClick={onSave}
                  >
                    Save
                  </Button>
                )}
                <Button
                  variant="SECONDARY"
                  size="SMALL"
                  onClick={signOut}
                >
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Hamburger menu */}
        {user && (
          <div className="header__mobile">
            <button
              className={`header__hamburger ${isMenuOpen ? 'header__hamburger--active' : ''}`}
              onClick={toggleMenu}
              type="button"
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
              <>
                <div className="header__overlay" onClick={closeMenu}></div>
                <div className="header__menu">
                  <div className="header__menu-header">
                    <span className="header__menu-user">
                      {user.displayName || (user.isAnonymous ? 'Guest' : 'User')}
                    </span>
                    <button
                      className="header__menu-close"
                      onClick={closeMenu}
                      type="button"
                      aria-label="Close menu"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="header__menu-content">
                    {saveState && (
                      <div className={`header__menu-save-status ${getSaveStatusClass()}`}>
                        {getSaveStatusText()}
                      </div>
                    )}

                    <div className="header__menu-section">
                      <div className="header__menu-item">
                        <span className="header__menu-label">Theme</span>
                        <ThemeToggle variant="toggle" showLabels={true} size="small" />
                      </div>
                    </div>

                    <div className="header__menu-actions">
                      {showCharacterSelect && onNavigateToCharacterSelect && (
                        <Button
                          variant="SECONDARY"
                          size="MEDIUM"
                          onClick={() => {
                            onNavigateToCharacterSelect();
                            closeMenu();
                          }}
                          className="header__menu-button"
                        >
                          👤 Characters
                        </Button>
                      )}
                      {onSave && saveState?.status === 'modified' && (
                        <Button
                          variant="PRIMARY"
                          size="MEDIUM"
                          onClick={() => {
                            onSave();
                            closeMenu();
                          }}
                          className="header__menu-button"
                        >
                          Save Changes
                        </Button>
                      )}
                      <Button
                        variant="SECONDARY"
                        size="MEDIUM"
                        onClick={() => {
                          signOut();
                          closeMenu();
                        }}
                        className="header__menu-button"
                      >
                        Logout
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;